import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SharedModule } from '../_shared/shared.module';
import {
  FormFieldList,
  SfCheckboxFormField,
  SfUtils,
  SmartFormComponent,
  SmartFormModule,
} from 'ngx-smart-form';
import { PresentationSheetTableComponent } from './presentation-sheet-table/presentation-sheet-table.component';
import { BudgetDetailsTableComponent } from './budget-details-table/budget-details-table.component';
import { TitlePercentageComponent } from '../_widgets/title-percentage/title-percentage.component';
import { SurveyComponent } from './survey/survey.component';
import { PresentationSheetService } from '../_core/services/presentation-sheet.service';
import {
  catchError,
  forkJoin,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { presentationSheetFields } from './presentation-sheet-fields';
import { PraticaProgressService } from '../_core/services/pratica-progress.service';
import { SurveyService } from '../_core/services/survey.service';
import { PresentationSheet } from './models/presentation-sheet';
import { PraticaSection } from '../pratica/models/pratica-section';
import { PersonaGiuridica } from '../applicant/models/persona-giuridica';
import { PersonaGiuridicaService } from '../_core/services/persona-giuridica.service';
import { ProductType } from '../requests/models/product';
import { Soggetto } from '../anagraphics/models/cms-document-fields';
import { FarmingInfoTableComponent } from './farming-info-table/farming-info-table.component';

@Component({
  selector: 'app-presentation-sheet',
  standalone: true,
  imports: [
    SharedModule,
    SmartFormModule,
    PresentationSheetTableComponent,
    BudgetDetailsTableComponent,
    TitlePercentageComponent,
    SurveyComponent,
    FarmingInfoTableComponent,
  ],
  templateUrl: './presentation-sheet.component.html',
  styleUrls: ['./presentation-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationSheetComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren(SmartFormComponent)
  smartFormComponentList?: QueryList<SmartFormComponent<PresentationSheet>>;

  @ViewChild(BudgetDetailsTableComponent)
  budgetDetailsTableComponent?: BudgetDetailsTableComponent;

  @ViewChild(FarmingInfoTableComponent)
  farmingInfoTableComponent?: FarmingInfoTableComponent;

  title: string = 'Scheda di presentazione';
  personaGiuridica?: PersonaGiuridica;

  idScheda: string = '';
  schedaPresentazione?: PresentationSheet;

  fields: FormFieldList<PresentationSheet> = [];

  subscription?: Subscription;

  presentationSheetFormCompletionPercentage = 0;

  get isFarmingIncluded() {
    return (
      this.smartFormComponentList?.get(3)?.formValue?.haveAllevamenti || false
    );
  }

  get showFarmingCheckbox() {
    return (
      this.praticaProgressService.selectedPratica?.products?.includes(
        'FINANZIAMENTO CRESCIAGRI'
      ) &&
      (this.praticaProgressService.selectedPratica?.tipoSoggetto === 'SP' ||
        this.praticaProgressService.selectedPratica?.tipoSoggetto === 'DI')
    );
  }

  constructor(
    private presentationSheetService: PresentationSheetService,
    private surveyService: SurveyService,
    public praticaProgressService: PraticaProgressService,
    private cdr: ChangeDetectorRef,
    private pgService: PersonaGiuridicaService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (!this.praticaProgressService.selectedPratica) return;
    let personaGiuridica$ = this.pgService.getById(
      this.praticaProgressService.selectedPratica.id,
      this.praticaProgressService.selectedPratica.target
    );

    let presentationSheet$ = this.presentationSheetService
      .getByPraticaId(this.praticaProgressService.selectedPratica.id)
      .pipe(catchError(() => of(undefined)));

    this.subscription = forkJoin([personaGiuridica$, presentationSheet$])
      .pipe(
        tap(([personaGiuridica, presentationSheet]) => {
          this.personaGiuridica = personaGiuridica;
          if (this.praticaProgressService.selectedPratica) {
            this.initData(presentationSheet);
          }
        }),
        switchMap(() =>
          this.checkBudgetDetailsCompletion(this.personaGiuridica!)
        ),
        switchMap((_) => this.praticaProgressService.triggerSave$),
        switchMap(() => this.save()),
        tap(() => {
          this.praticaProgressService.notifySectionUpdate();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private checkBudgetDetailsCompletion(personaGiuridica: PersonaGiuridica) {
    if (personaGiuridica?.tipoSoggetto !== 'SC' && !this.showFarmingCheckbox) {
      return of(null);
    } else {
      let secondaryCompletionPercentage$ = this.showFarmingCheckbox
        ? this.farmingInfoTableComponent!.completionPercentage
        : this.budgetDetailsTableComponent!.completionPercentage;
      return secondaryCompletionPercentage$.pipe(
        tap((percentage) => {
          let totalProgress =
            this.presentationSheetFormCompletionPercentage + percentage;
          this.praticaProgressService.updateProgress(
            PraticaSection.PRESENTATION_SHEET,
            Math.round(totalProgress)
          );
        })
      );
    }
  }

  private initData(res: PresentationSheet | undefined) {
    let allFields = presentationSheetFields(res);
    this.fields = allFields;

    this.idScheda = res?.id || '';
    this.schedaPresentazione = res!;
    this.cdr.detectChanges();
    if (this.praticaProgressService.isFrozen) {
      this.smartFormComponentList?.forEach((c) =>
        c.form.disable({ emitEvent: false, onlySelf: true })
      );
    }
    this.praticaProgressService
      .trackFormChanges([...(this.smartFormComponentList?.toArray() || [])])
      .subscribe((res) => {
        this.updateCompletionPercentage(res);
      });
  }

  private save() {
    let presentationSheetData = SfUtils.aggregateFormsValues(
      this.smartFormComponentList?.toArray()
    );
    let sheet: PresentationSheet = {
      ...this.schedaPresentazione,
      ...presentationSheetData,
    } as PresentationSheet;

    return this.presentationSheetService.edit(
      sheet,
      this.schedaPresentazione!.id
    );
  }

  private updateCompletionPercentage(completionPercentage: number) {
    let pSFormWeight =
      this.personaGiuridica?.tipoSoggetto === 'SC' || this.showFarmingCheckbox
        ? 2 / 3
        : 1;
    this.presentationSheetFormCompletionPercentage =
      this.praticaProgressService.calcComplexCompletionPercentage(
        completionPercentage,
        pSFormWeight
      );

    let secondaryComponentCompletionPercentage = 0;
    if (
      this.personaGiuridica?.tipoSoggetto === 'SC' ||
      this.showFarmingCheckbox
    ) {
      secondaryComponentCompletionPercentage = this.showFarmingCheckbox
        ? this.farmingInfoTableComponent?.completionPercentage.value || 0
        : this.budgetDetailsTableComponent?.completionPercentage.value || 0;
    }
    let totalProgress =
      this.presentationSheetFormCompletionPercentage +
      secondaryComponentCompletionPercentage;
    this.praticaProgressService.updateProgress(
      PraticaSection.PRESENTATION_SHEET,
      Math.round(totalProgress)
    );
  }
}
